import { useEffect, useState } from "react";
import { Button, Toggle } from "@/components/ui";
import { Icon } from "@/components/IconSprite";

export interface WidgetConfigEditorProps {
  publicKey: string | null;
  initialOverrides?: Record<string, unknown>;
  isSaving?: boolean;
  onSave: (overrides: Record<string, unknown>) => void;
  onDelete?: () => void;
}

type NavKey = "home" | "messages" | "help" | "news";

interface FormState {
  themeMode: string;
  position: string;
  expandedPosition: string;
  primaryColor: string;
  welcomeMessage: string;
  quickReplies: string[];
  features: {
    attachments: boolean;
    voice: boolean;
    tooltips: boolean;
    quickReplies: boolean;
    streaming: boolean;
    streamDelayMs: number;
    streamChunkSize: number;
  };
  nav: Record<NavKey, boolean>;
}

// Display order in the editor mirrors the bottom-nav order in the widget itself.
const NAV_ITEMS: Array<{ key: NavKey; label: string; description: string }> = [
  { key: "home", label: "Home", description: "Landing panel with recent conversations and quick actions." },
  { key: "messages", label: "Messages", description: "Active chat thread. Disabling this would hide the chat itself — not recommended." },
  { key: "help", label: "Help", description: "Help articles / knowledge base panel." },
  { key: "news", label: "News", description: "Announcements and broadcast posts." },
];

const DEFAULT_FORM_STATE: FormState = {
  themeMode: "light",
  position: "right",
  expandedPosition: "launcher",
  primaryColor: "#00a850",
  welcomeMessage: "Hi there! I'm the Sante Assistant. How can I help today?",
  quickReplies: [
    "Show me today's highlights",
    "Summarize recent member activity",
    "What promotions are active?",
  ],
  features: {
    attachments: true,
    voice: false,
    tooltips: false,
    quickReplies: true,
    streaming: true,
    streamDelayMs: 55,
    streamChunkSize: 6,
  },
  nav: {
    home: true,
    messages: true,
    help: true,
    news: true,
  },
};

function mergeInitialState(initial?: Record<string, unknown>): FormState {
  if (!initial) return DEFAULT_FORM_STATE;

  const theme = (initial.theme as Record<string, unknown>) ?? {};
  const tokens = (theme.tokens as Record<string, string>) ?? {};
  const features = (initial.features as Record<string, unknown>) ?? {};

  const toNumber = (value: unknown, fallback: number): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
  };

  const featureRecord = features as Record<string, unknown>;
  const streamDelayRaw = featureRecord["streamDelayMs"] ?? featureRecord["stream_delay_ms"];
  const streamChunkRaw = featureRecord["streamChunkSize"] ?? featureRecord["stream_chunk_size"];

  return {
    themeMode: String(theme.mode ?? DEFAULT_FORM_STATE.themeMode),
    position: String(theme.position ?? DEFAULT_FORM_STATE.position),
    expandedPosition: String(
      ((initial.layout as Record<string, unknown>) ?? {}).expandedPosition ?? DEFAULT_FORM_STATE.expandedPosition
    ),
    primaryColor: String(
      tokens["--accent"] ?? tokens["--bpractai-launcher-background"] ?? DEFAULT_FORM_STATE.primaryColor
    ),
    welcomeMessage: String(
      ((initial.welcome as Record<string, unknown>) ?? {}).message ?? DEFAULT_FORM_STATE.welcomeMessage
    ),
    quickReplies: Array.isArray(initial.quickReplies)
      ? (initial.quickReplies as string[])
      : DEFAULT_FORM_STATE.quickReplies,
    features: {
      attachments: Boolean(features.attachments ?? DEFAULT_FORM_STATE.features.attachments),
      voice: Boolean(features.voice ?? DEFAULT_FORM_STATE.features.voice),
      tooltips: Boolean(features.tooltips ?? DEFAULT_FORM_STATE.features.tooltips),
      quickReplies: Boolean(features.quickReplies ?? DEFAULT_FORM_STATE.features.quickReplies),
      streaming: Boolean(features.streaming ?? DEFAULT_FORM_STATE.features.streaming),
      streamDelayMs: toNumber(streamDelayRaw, DEFAULT_FORM_STATE.features.streamDelayMs),
      streamChunkSize: Math.max(1, Math.floor(toNumber(streamChunkRaw, DEFAULT_FORM_STATE.features.streamChunkSize))),
    },
    nav: ((): Record<NavKey, boolean> => {
      const navOverride = (initial.nav as Record<string, unknown>) ?? {};
      const result: Record<NavKey, boolean> = { ...DEFAULT_FORM_STATE.nav };
      (Object.keys(result) as NavKey[]).forEach((key) => {
        if (key in navOverride) {
          result[key] = navOverride[key] !== false;
        }
      });
      return result;
    })(),
  };
}

const WidgetConfigEditor = ({ publicKey, initialOverrides, onSave, onDelete, isSaving }: WidgetConfigEditorProps) => {
  const [state, setState] = useState<FormState>(() => mergeInitialState(initialOverrides));

  useEffect(() => {
    setState(mergeInitialState(initialOverrides));
  }, [publicKey, initialOverrides]);

  const handleQuickReplyChange = (index: number, value: string) => {
    setState((prev) => {
      const next = [...prev.quickReplies];
      next[index] = value;
      return { ...prev, quickReplies: next };
    });
  };

  const addQuickReply = () => {
    setState((prev) => ({ ...prev, quickReplies: [...prev.quickReplies, ""] }));
  };

  const removeQuickReply = (index: number) => {
    setState((prev) => ({ ...prev, quickReplies: prev.quickReplies.filter((_, i) => i !== index) }));
  };

  const updateStreamingSetting = (key: "streamDelayMs" | "streamChunkSize", rawValue: string) => {
    setState((prev) => {
      const parsed = Number(rawValue);
      const fallback = DEFAULT_FORM_STATE.features[key];
      let next = Number.isFinite(parsed) ? parsed : fallback;
      next = key === "streamChunkSize" ? Math.max(1, Math.floor(next)) : Math.max(0, next);
      return { ...prev, features: { ...prev.features, [key]: next } };
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({
      theme: {
        mode: state.themeMode,
        position: state.position,
        tokens: {
          "--bpractai-launcher-background": state.primaryColor,
          "--accent": state.primaryColor,
        },
      },
      quickReplies: state.quickReplies.filter(Boolean),
      welcome: { message: state.welcomeMessage },
      features: state.features,
      layout: { expandedPosition: state.expandedPosition },
      nav: state.nav,
    });
  };

  const quickRepliesEnabled = state.features.quickReplies;

  const featureToggles: Array<{ key: string; label: string }> = [
    { key: "quickReplies", label: "Show quick replies" },
    { key: "voice", label: "Enable voice input" },
    { key: "streaming", label: "Stream responses" },
  ];

  return (
    <form onSubmit={handleSubmit} className="md c" style={{ gridTemplateColumns: "1fr 380px" }}>
      {/* Config form */}
      <div className="card">
        <div className="card-h">
          <div>
            <h2>Appearance &amp; behavior</h2>
            <p>Changes apply to every embedded widget on save.</p>
          </div>
        </div>
        <div className="card-b">
          {/* Appearance */}
          <div className="sec">
            <div className="sec-h">
              <b>
                <span className="sec-n">1</span>Appearance
              </b>
            </div>
            <div className="row c2">
              <div className="fg">
                <label className="fl">Theme</label>
                <select
                  className="sel"
                  value={state.themeMode}
                  onChange={(e) => setState((prev) => ({ ...prev, themeMode: e.target.value }))}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">Match system</option>
                </select>
              </div>
              <div className="fg">
                <label className="fl">Position</label>
                <select
                  className="sel"
                  value={state.position}
                  onChange={(e) => setState((prev) => ({ ...prev, position: e.target.value }))}
                >
                  <option value="right">Bottom right</option>
                  <option value="left">Bottom left</option>
                </select>
              </div>
            </div>
            <div className="row c2">
              <div className="fg">
                <label className="fl">Expanded Position</label>
                <select
                  className="sel"
                  value={state.expandedPosition}
                  onChange={(e) => setState((prev) => ({ ...prev, expandedPosition: e.target.value }))}
                >
                  <option value="launcher">Launcher anchored</option>
                  <option value="center">Centered modal</option>
                  <option value="docked">Docked panel</option>
                  <option value="expanded">Full height</option>
                </select>
              </div>
              <div className="fg">
                <label className="fl">Accent Color</label>
                <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                  <input
                    type="color"
                    value={state.primaryColor}
                    onChange={(e) => setState((prev) => ({ ...prev, primaryColor: e.target.value }))}
                    style={{
                      height: 34,
                      width: 44,
                      borderRadius: 8,
                      border: "1px solid var(--line-2)",
                      cursor: "pointer",
                      flexShrink: 0,
                      padding: 2,
                      background: "none",
                    }}
                  />
                  <span className="mono" style={{ color: "var(--ink-3)" }}>
                    {state.primaryColor}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="sec">
            <div className="sec-h">
              <b>
                <span className="sec-n">2</span>Features
              </b>
            </div>
            {featureToggles.map((item, i) => (
              <div
                key={item.key}
                className="tg-row"
                style={i < featureToggles.length - 1 ? { borderBottom: "1px solid var(--line)" } : undefined}
              >
                <span className="tx">
                  <b>{item.label}</b>
                </span>
                <Toggle
                  checked={state.features[item.key as keyof typeof state.features] as boolean}
                  onChange={(checked) =>
                    setState((prev) => ({ ...prev, features: { ...prev.features, [item.key]: checked } }))
                  }
                />
              </div>
            ))}

            {state.features.streaming && (
              <div className="row c2" style={{ marginTop: 14 }}>
                <div className="fg">
                  <label className="fl">Stream Delay (ms)</label>
                  <input
                    className="inp"
                    type="number"
                    min={0}
                    step={5}
                    value={state.features.streamDelayMs}
                    onChange={(e) => updateStreamingSetting("streamDelayMs", e.target.value)}
                  />
                </div>
                <div className="fg">
                  <label className="fl">Chunk Size</label>
                  <input
                    className="inp"
                    type="number"
                    min={1}
                    step={1}
                    value={state.features.streamChunkSize}
                    onChange={(e) => updateStreamingSetting("streamChunkSize", e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Navigation tabs (footer) */}
          <div className="sec">
            <div className="sec-h">
              <b>
                <span className="sec-n">3</span>Navigation tabs
              </b>
              <span>Show or hide each tab in the widget's bottom navigation.</span>
            </div>
            {NAV_ITEMS.map((item, i) => (
              <div
                key={item.key}
                className="tg-row"
                style={i < NAV_ITEMS.length - 1 ? { borderBottom: "1px solid var(--line)" } : undefined}
              >
                <span className="tx">
                  <b>{item.label}</b>
                  <span>{item.description}</span>
                </span>
                <Toggle
                  checked={state.nav[item.key]}
                  onChange={(checked) =>
                    setState((prev) => ({ ...prev, nav: { ...prev.nav, [item.key]: checked } }))
                  }
                />
              </div>
            ))}
          </div>

          {/* Welcome Message */}
          <div className="sec">
            <div className="sec-h">
              <b>
                <span className="sec-n">4</span>Welcome Message
              </b>
            </div>
            <div className="fg">
              <textarea
                className="ta"
                value={state.welcomeMessage}
                onChange={(e) => setState((prev) => ({ ...prev, welcomeMessage: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          {/* Quick Replies */}
          <div className="sec">
            <div
              className="sec-h"
              style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}
            >
              <b>
                <span className="sec-n">5</span>Quick Replies
              </b>
              <Button type="button" variant="secondary" size="sm" onClick={addQuickReply} disabled={!quickRepliesEnabled}>
                Add Reply
              </Button>
            </div>
            {!quickRepliesEnabled && (
              <p className="hint" style={{ marginBottom: 12 }}>
                Enable "Show quick replies" to edit these.
              </p>
            )}
            {state.quickReplies.map((reply, index) => (
              <div key={index} className="fg" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  className="inp"
                  style={{ flex: 1 }}
                  value={reply}
                  onChange={(e) => handleQuickReplyChange(index, e.target.value)}
                  disabled={!quickRepliesEnabled}
                  placeholder={`Quick reply ${index + 1}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeQuickReply(index)}
                  disabled={!quickRepliesEnabled || state.quickReplies.length <= 1}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div className="card-f">
          <div className="r">
            <Button type="submit" loading={isSaving}>
              Save Changes
            </Button>
            {onDelete && (
              <Button type="button" variant="danger" onClick={onDelete}>
                Delete Configuration
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="card" style={{ position: "sticky", top: 14 }}>
        <div className="card-h tight">
          <div>
            <h2>Live preview</h2>
          </div>
          <div className="r">
            <span className="b def">{state.themeMode === "dark" ? "Dark" : "Light"}</span>
          </div>
        </div>
        <div className="card-b" style={{ padding: 0 }}>
          <div className="wprev">
            <div className="wbub">
              <div className="wbub-h" style={{ background: state.primaryColor }}>
                <span className="a2">
                  <Icon name="leaf" />
                </span>
                <span>
                  <b>Santé Assistant</b>
                  <span>Ask about products, orders, or your business</span>
                </span>
              </div>
              <div className="wbub-b">
                <div className="bub a" style={{ maxWidth: "100%", fontSize: 12, padding: "8px 11px" }}>
                  {state.welcomeMessage}
                </div>
                {quickRepliesEnabled && state.quickReplies.filter(Boolean)[0] && (
                  <div
                    className="bub u"
                    style={{ maxWidth: "80%", fontSize: 12, padding: "8px 11px", background: state.primaryColor }}
                  >
                    {state.quickReplies.filter(Boolean)[0]}
                  </div>
                )}
              </div>
              <div className="wbub-f">
                <span className="fake-in">Type a message…</span>
                <span className="snd" style={{ background: state.primaryColor }}>
                  <Icon name="send" />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="card-f">
          <span className="meta">Preview reflects unsaved changes.</span>
        </div>
      </div>
    </form>
  );
};

export default WidgetConfigEditor;
